import { Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLink, IconQuestionMark, IconUnlink } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageTitle } from 'components/index';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { isEmpty, isNonNull, isNullish } from 'remeda';
import {
  CompanyWebCredentialsSchema,
  useSaveCompanyWebCredentials,
} from 'shared/hooks/use-save-company-web-credentials';

export const CompanyWeb = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const _history = useHistory();
  const queryClient = useQueryClient();

  const companyWebCredentials = company.company_web_credentials;
  const { saveCompanyWebCredentials, isSaveCompanyWebCredentialsLoading } =
    useSaveCompanyWebCredentials();

  const form = useForm({
    initialValues: {
      login: companyWebCredentials.login ?? '',
      password: companyWebCredentials.password ?? '',
    },
  });

  useEffect(() => {
    form.setFieldValue('login', companyWebCredentials.login ?? '');
    form.setFieldValue('password', companyWebCredentials.password ?? '');
  }, [companyWebCredentials]);

  const isCompanyWebConnected =
    isNonNull(companyWebCredentials.login) && isNonNull(companyWebCredentials.password);

  const isSaveButtonDisabled =
    isNullish(form.values.login) ||
    isEmpty(form.values.login) ||
    isNullish(form.values.password) ||
    isEmpty(form.values.password);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const data = CompanyWebCredentialsSchema.parse(form.values);
    saveCompanyWebCredentials(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['company-configuration'] });
      },
    });
  };

  const handleReset = () => {
    saveCompanyWebCredentials(
      { password: null, login: null },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['company-configuration'] });
        },
      },
    );
  };

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        CompanyWeb
      </PageTitle>
      <Stack>
        <Panel
          footer={
            <Button disabled={!form.isDirty()} type="submit" loading={form.submitting}>
              {t(i18nKeys.SAVE)}
            </Button>
          }
        >
          {!company.package?.can_use_monitoring ? (
            <Stack align="center">
              <Text>{t(i18nKeys.ACCOUNT_LIMITATION)}</Text>
              <Button onClick={() => window.Intercom('show')} leftSection={<IconQuestionMark />}>
                {t(i18nKeys.MORE_INFO)}
              </Button>
            </Stack>
          ) : (
            <Stack w="33%" style={{ margin: 'auto' }}>
              <form onSubmit={handleSubmit}>
                <TextInput
                  {...form.getInputProps('login')}
                  disabled={isCompanyWebConnected}
                  label={t(i18nKeys.USERNAME)}
                  placeholder={t(i18nKeys.USERNAME)}
                  mb="md"
                />
                <TextInput
                  {...form.getInputProps('password')}
                  disabled={isCompanyWebConnected}
                  label={t(i18nKeys.PASSWORD)}
                  placeholder={t(i18nKeys.PASSWORD)}
                  mb={38}
                />
                {isCompanyWebConnected ? (
                  <Button
                    leftSection={<IconUnlink stroke={1.5} />}
                    color="red"
                    w="100%"
                    onClick={handleReset}
                  >
                    {t(i18nKeys.RESET)}
                  </Button>
                ) : (
                  <Button
                    leftSection={<IconLink stroke={1.5} />}
                    disabled={isSaveButtonDisabled}
                    loading={isSaveCompanyWebCredentialsLoading}
                    w="100%"
                    type="submit"
                  >
                    {t(i18nKeys.SAVE)}
                  </Button>
                )}
              </form>
            </Stack>
          )}
        </Panel>
      </Stack>
    </>
  );
};
